// =============================================================================
// SIREPPAR - Página de Reportes
// =============================================================================

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import { api } from '../../services/api';

// =============================================================================
// Tipos
// =============================================================================

interface EstadisticasPartidos {
  total: number;
  porEstado: Record<string, number>;
  porTipo: Record<string, number>;
}

interface EstadisticasExpedientes {
  total: number;
  porEstado: Record<string, number>;
  porTipo: Record<string, number>;
  vencidos: number;
  promedioResolucion: number;
}

// =============================================================================
// Constantes
// =============================================================================

const estadoPartidoLabels: Record<string, string> = {
  EN_FORMACION: 'Creados (Aun no en revisión)',
  PENDIENTE_REVISION: 'En Revisión',
  RECONOCIDO: 'Reconocido',
  VIGENTE: 'Vigente',
  CADUCO: 'Caduco',
  EXTINGUIDO: 'Extinguido',
  SUSPENDIDO: 'Suspendido',
  FUSIONADO: 'Fusionado',
  ABSORBIDO: 'Absorbido',
};

const estadoExpedienteLabels: Record<string, string> = {
  BORRADOR: 'Borrador',
  PRESENTADO: 'Presentado',
  EN_ADMISIBILIDAD: 'En Admisibilidad',
  INTIMADO: 'Intimado',
  EN_ANALISIS: 'En Análisis',
  OBSERVADO: 'Observado (en análisis)',
  EN_SECRETARIO: 'En Secretario',
  EN_TEP: 'En TEP',
  REC_PROVISORIO: 'Rec. Provisorio',
  PENDIENTE_RESOLUCION: 'En Secretario',
  APROBADO: 'Aprobado',
  RECHAZADO: 'Rechazado',
  VIGENTE: 'Vigente',
  ARCHIVADO: 'Archivado',
  NO_PRESENTADO: 'No Presentado',
};

// Estados que se muestran en las barras (en este orden; el resto queda excluido).
// EN_FORMACION = partidos creados que todavía no entraron en revisión.
const ordenEstadoPartido = [
  'EN_FORMACION', 'PENDIENTE_REVISION', 'RECONOCIDO', 'VIGENTE',
];
const ordenEstadoExpediente = [
  'PRESENTADO', 'EN_ADMISIBILIDAD', 'INTIMADO', 'EN_ANALISIS', 'OBSERVADO',
  'EN_SECRETARIO', 'EN_TEP', 'APROBADO', 'ARCHIVADO', 'NO_PRESENTADO', 'BORRADOR',
  // Legacy: nombre anterior de EN_SECRETARIO (expedientes sin migrar).
  'PENDIENTE_RESOLUCION',
];

const tipoTramiteLabels: Record<string, string> = {
  RECONOCIMIENTO: 'Reconocimiento',
  REEMPADRONAMIENTO: 'Reempadronamiento',
  CAMBIO_AUTORIDADES: 'Cambio de Autoridades',
  CAMBIO_DENOMINACION: 'Cambio de Denominación',
  FUSION: 'Fusión',
  CONFEDERACION: 'Confederación',
  SUBSANACION: 'Subsanación',
  CADUCIDAD: 'Caducidad',
  EXTINCION: 'Extinción',
  RENDICION_ANUAL: 'Rendición Anual',
};
const coloresTipoTramite: Record<string, string> = {
  RECONOCIMIENTO: 'bg-green-500',
  REEMPADRONAMIENTO: 'bg-blue-500',
  CAMBIO_AUTORIDADES: 'bg-purple-500',
  CAMBIO_DENOMINACION: 'bg-purple-400',
  FUSION: 'bg-pink-500',
  CONFEDERACION: 'bg-indigo-500',
  SUBSANACION: 'bg-orange-500',
  CADUCIDAD: 'bg-red-400',
  EXTINCION: 'bg-red-600',
  RENDICION_ANUAL: 'bg-teal-500',
};

// Solo ámbitos territoriales. El reporte de partidos agrupa por `tipo` y cuenta solo
// agrupaciones de clase ESTANDAR, así que CONFEDERACION ya no puede aparecer como
// categoría: dejó de ser un `TipoPartido` y pasó a ser un valor de `Partido.clase`.
const tipoPartidoLabels: Record<string, string> = {
  MUNICIPAL: 'Municipal',
  DISTRITO: 'Distrito',
  PROVINCIAL: 'Provincial',
  NACIONAL: 'Nacional',
};
const coloresTipoPartido: Record<string, string> = {
  MUNICIPAL: 'bg-teal-500',
  DISTRITO: 'bg-green-500',
  PROVINCIAL: 'bg-blue-500',
  NACIONAL: 'bg-purple-500',
};

const coloresEstadoPartido: Record<string, string> = {
  EN_FORMACION: 'bg-yellow-500',
  PENDIENTE_REVISION: 'bg-blue-500',
  RECONOCIDO: 'bg-blue-500',
  VIGENTE: 'bg-green-500',
  CADUCO: 'bg-orange-500',
  EXTINGUIDO: 'bg-red-500',
  SUSPENDIDO: 'bg-gray-500',
  FUSIONADO: 'bg-purple-500',
  ABSORBIDO: 'bg-purple-400',
};

const coloresEstadoExpediente: Record<string, string> = {
  BORRADOR: 'bg-gray-400',
  PRESENTADO: 'bg-blue-500',
  EN_ADMISIBILIDAD: 'bg-cyan-500',
  INTIMADO: 'bg-amber-600',
  EN_ANALISIS: 'bg-yellow-500',
  OBSERVADO: 'bg-orange-500',
  EN_SECRETARIO: 'bg-indigo-500',
  EN_TEP: 'bg-cyan-600',
  PENDIENTE_RESOLUCION: 'bg-indigo-500',  // legacy
  APROBADO: 'bg-green-500',
  RECHAZADO: 'bg-red-500',
  ARCHIVADO: 'bg-gray-300',
  NO_PRESENTADO: 'bg-gray-500',
};

// =============================================================================
// Componente
// =============================================================================

export default function ReportesPage() {
  const [periodo, setPeriodo] = useState('mes');
  const [activeTab, setActiveTab] = useState<'partidos' | 'expedientes'>('partidos');

  // Query estadísticas partidos
  const { data: statsPartidos, isLoading: loadingPartidos } = useQuery({
    queryKey: ['stats-partidos', periodo],
    queryFn: async () => {
      const response = await api.get('/reportes/estadisticas/partidos', { params: { periodo } });
      return response.data.data as EstadisticasPartidos;
    },
  });

  // Query estadísticas expedientes
  const { data: statsExpedientes, isLoading: loadingExpedientes } = useQuery({
    queryKey: ['stats-expedientes', periodo],
    queryFn: async () => {
      const response = await api.get('/reportes/estadisticas/expedientes', { params: { periodo } });
      return response.data.data as EstadisticasExpedientes;
    },
  });

  // Devuelve SOLO los estados del orden dado (los faltantes en 0, para que se vean
  // aunque no haya registros). Cualquier estado no listado queda excluido de la barra.
  const ordenar = (data: Record<string, number> = {}, orden: string[]): Record<string, number> => {
    const result: Record<string, number> = {};
    orden.forEach((k) => { result[k] = data?.[k] || 0; });
    return result;
  };

  const BarChart = ({ data, labels, colors }: { data: Record<string, number>; labels: Record<string, string>; colors: Record<string, string> }) => {
    const total = Object.values(data).reduce((a, b) => a + b, 0);
    if (total === 0) return <p className="text-gray-500">Sin datos</p>;
    
    return (
      <div className="space-y-3">
        {Object.entries(data).map(([key, value]) => {
          const percentage = (value / total) * 100;
          return (
            <div key={key}>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-gray-700">{labels[key] || key}</span>
                <span className="font-medium">{value} ({percentage.toFixed(1)}%)</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div
                  className={`h-3 rounded-full ${colors[key] || 'bg-gray-500'}`}
                  style={{ width: `${percentage}%` }}
                />
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const periodoLabels: Record<string, string> = {
    semana: 'Última semana',
    mes: 'Último mes',
    trimestre: 'Último trimestre',
    'año': 'Último año',
    todo: 'Todo',
  };

  return (
    <div className="space-y-6 reportes-print-root">
      <style>{`
        @media print {
          body * { visibility: hidden !important; }
          .reportes-print-area, .reportes-print-area * { visibility: visible !important; }
          .reportes-print-area { position: absolute; left: 0; top: 0; width: 100%; }
          .reportes-print-area .bg-white { box-shadow: none !important; border: 1px solid #e5e7eb !important; }
          .reportes-no-print { display: none !important; }
          @page { size: portrait; margin: 10mm; }

          /* Todo en una hoja: sin cortes de página dentro de los bloques */
          .reportes-print-area > div { page-break-inside: avoid; break-inside: avoid; }
          .reportes-charts > div { page-break-inside: avoid; break-inside: avoid; }
          .reportes-print-area { gap: 0.75rem !important; }

          /* KPIs: 4 cuadrados compactos en una sola fila */
          .reportes-kpis {
            grid-template-columns: repeat(4, 1fr) !important;
            gap: 0.5rem !important;
            margin-bottom: 0.75rem !important;
          }
          .reportes-kpis > div { padding: 0.6rem !important; }
          .reportes-kpis .text-3xl { font-size: 1.25rem !important; margin-top: 0.15rem !important; }
          .reportes-kpis .text-sm { font-size: 0.7rem !important; }

          /* Gráficos: dos columnas, compactos */
          .reportes-charts {
            grid-template-columns: repeat(2, 1fr) !important;
            gap: 0.75rem !important;
          }
          .reportes-charts > div { padding: 0.75rem !important; }
          .reportes-charts h3 { font-size: 0.85rem !important; margin-bottom: 0.5rem !important; }
          .reportes-charts .space-y-3 > * + * { margin-top: 0.35rem !important; }
        }
      `}</style>

      {/* Header */}
      <div className="sm:flex sm:items-center sm:justify-between reportes-no-print">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Reportes y Estadísticas</h1>
          <p className="mt-1 text-sm text-gray-500">Resumen del estado del sistema</p>
        </div>
        <div className="mt-4 sm:mt-0 flex gap-2">
          <select
            value={periodo}
            onChange={(e) => setPeriodo(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2"
          >
            <option value="semana">Última semana</option>
            <option value="mes">Último mes</option>
            <option value="trimestre">Último trimestre</option>
            <option value="año">Último año</option>
            <option value="todo">Todo</option>
          </select>
          <button
            onClick={() => window.print()}
            className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            <ArrowDownTrayIcon className="h-5 w-5" />
            Exportar PDF
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 reportes-no-print">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('partidos')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'partidos'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Partidos Políticos
          </button>
          <button
            onClick={() => setActiveTab('expedientes')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'expedientes'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Expedientes
          </button>
        </nav>
      </div>

      {/* Content (área imprimible) */}
      <div className="reportes-print-area space-y-6">
        {/* Encabezado solo para impresión/PDF */}
        <div className="hidden print:block mb-2">
          <h1 className="text-xl font-bold text-gray-900">Reportes y Estadísticas</h1>
          <p className="text-sm text-gray-600">
            {activeTab === 'partidos' ? 'Partidos Políticos' : 'Expedientes'} · Período: {periodoLabels[periodo] || periodo}
          </p>
        </div>

      {activeTab === 'partidos' && (
        <div className="space-y-6">
          {/* KPIs */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 reportes-kpis">
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Total Partidos</p>
              <p className="text-3xl font-bold text-gray-900 mt-2">
                {loadingPartidos ? '...' : statsPartidos?.total || 0}
              </p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">En Revisión</p>
              <p className="text-3xl font-bold text-yellow-600 mt-2">
                {loadingPartidos ? '...' : statsPartidos?.porEstado?.PENDIENTE_REVISION || 0}
              </p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Reconocidos</p>
              <p className="text-3xl font-bold text-blue-600 mt-2">
                {loadingPartidos ? '...' : statsPartidos?.porEstado?.RECONOCIDO || 0}
              </p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Vigentes</p>
              <p className="text-3xl font-bold text-green-600 mt-2">
                {loadingPartidos ? '...' : statsPartidos?.porEstado?.VIGENTE || 0}
              </p>
            </div>
          </div>

          {/* Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 reportes-charts">
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Por Estado</h3>
              {loadingPartidos ? (
                <div className="h-48 flex items-center justify-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" /></div>
              ) : (
                <BarChart
                  data={ordenar(statsPartidos?.porEstado, ordenEstadoPartido)}
                  labels={estadoPartidoLabels}
                  colors={coloresEstadoPartido}
                />
              )}
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Por Tipo</h3>
              {loadingPartidos ? (
                <div className="h-48 flex items-center justify-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" /></div>
              ) : (
                <BarChart
                  data={statsPartidos?.porTipo || {}}
                  labels={tipoPartidoLabels}
                  colors={coloresTipoPartido}
                />
              )}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'expedientes' && (
        <div className="space-y-6">
          {/* KPIs */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 reportes-kpis">
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Total Expedientes</p>
              <p className="text-3xl font-bold text-gray-900 mt-2">
                {loadingExpedientes ? '...' : statsExpedientes?.total || 0}
              </p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Presentados</p>
              <p className="text-3xl font-bold text-blue-600 mt-2">
                {loadingExpedientes ? '...' : statsExpedientes?.porEstado?.PRESENTADO || 0}
              </p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">En Revisión</p>
              <p className="text-3xl font-bold text-yellow-600 mt-2">
                {loadingExpedientes ? '...' : (statsExpedientes?.porEstado?.EN_ADMISIBILIDAD || 0) + (statsExpedientes?.porEstado?.INTIMADO || 0) + (statsExpedientes?.porEstado?.EN_ANALISIS || 0) + (statsExpedientes?.porEstado?.OBSERVADO || 0)}
              </p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">En Secretario</p>
              <p className="text-3xl font-bold text-indigo-600 mt-2">
                {loadingExpedientes
                  ? '...'
                  : (statsExpedientes?.porEstado?.EN_SECRETARIO || 0) +
                    // Legacy: mismo estado con su nombre anterior.
                    (statsExpedientes?.porEstado?.PENDIENTE_RESOLUCION || 0)}
              </p>
            </div>
          </div>

          {/* Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 reportes-charts">
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Por Estado</h3>
              {loadingExpedientes ? (
                <div className="h-48 flex items-center justify-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" /></div>
              ) : (
                <BarChart
                  data={ordenar(statsExpedientes?.porEstado, ordenEstadoExpediente)}
                  labels={estadoExpedienteLabels}
                  colors={coloresEstadoExpediente}
                />
              )}
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Por Tipo de Trámite</h3>
              {loadingExpedientes ? (
                <div className="h-48 flex items-center justify-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" /></div>
              ) : (
                <BarChart
                  data={statsExpedientes?.porTipo || {}}
                  labels={tipoTramiteLabels}
                  colors={coloresTipoTramite}
                />
              )}
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
}
